// WHERE THE STARS OF A GALAXY GO — the shape of the map, its companions, and how it turns.
//
// The galaxy view used to place stars by rejection sampling inside a flat disc: pick an angle and a radius, roll
// against a density, reject on collision, repeat. That produced one shape for every galaxy in the game, with a
// hard rim, and it assigned each star's INDEX in acceptance order. This module replaces all of it.
//
// THREE THINGS ABOUT IT ARE LOAD-BEARING.
//
// 1. AN ADDRESS IS AN INDEX, not a coordinate. `930718.1015` names the 1015th system of galaxy 930718, and both
//    positionType() and generateSystem() are keyed on that integer. So moving every star is safe: a system keeps
//    its star, its planets, its claims and its bookmarks, and only its place on the map changes. Nothing here
//    may ever change WHICH index a system is.
//
// 2. EVERY INDEX 1…N GETS A POSITION, directly, with no rejection loop. The old loop happened to place 100% of
//    its target (measured across eight galaxies, ~2.2x of its 12x attempt budget) but nothing guaranteed it — and
//    a shape that rejected more would have left claimed systems existing, reachable by URL, and invisible on the
//    map. Every shape here is a function of the index.
//
// 3. THE RIM FADES. Density falls to zero before the radius rather than stopping at it, so nothing piles up on a
//    boundary and nothing lands outside. That is why there is no clamp anywhere below.
//
// Shapes are ROLLED PER GALAXY from its own seed, weighted by how common each kind should be, so a galaxy always
// looks like itself. See tests/galaxy-shape.test.mjs for what is pinned.

// The PRNG is app.js's createSeededRandom character for character. It is duplicated rather than imported because
// this module must not depend on the app shell; the test asserts the two stay identical.
export function seeded(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = Math.imul(31, h) + seed.charCodeAt(i) | 0;
  return function() {
    let t = h += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  }
}

const TAU = Math.PI * 2;
const gauss = (a, b) => Math.sqrt(-2 * Math.log(1e-9 + a)) * Math.cos(TAU * b);
const clampU = (v) => v < -1 ? -1 : v > 1 ? 1 : v;

// ---------------------------------------------------------------------------------------------------- THE RIM
// Full density inside `hold`, then smoothly to zero AT the radius — zero value and zero slope, so there is no
// visible edge and no last-annulus pile-up.
const taper = (r, hold) => {
  if (r <= hold) return 1;
  const t = (r - hold) / (1 - hold);
  return Math.pow(1 - t * t, 2.4);
};
// A CDF over r with the area element folded in, inverted by binary search. Built ONCE per galaxy per shape —
// per star it would cost 640 density evaluations each.
function radialSampler(dens, hold) {
  const n = 640, cdf = new Float64Array(n + 1);
  let acc = 0;
  for (let k = 0; k <= n; k++) { const r = k / n; acc += dens(r) * taper(r, hold) * r; cdf[k] = acc; }
  if (acc <= 0) return () => 0;
  for (let k = 0; k <= n; k++) cdf[k] /= acc;
  return (u) => {
    let lo = 0, hi = n;
    while (lo < hi) { const mid = (lo + hi) >> 1; if (cdf[mid] < u) lo = mid + 1; else hi = mid; }
    const k = lo < 1 ? 1 : lo, a = cdf[k - 1], b = cdf[k];
    return (k - 1 + (b > a ? (u - a) / (b - a) : 0)) / n;
  };
}
// For the shapes whose structure is not radial — clumps, pairs, streams — a point can still stray past the rim.
// Folded back smoothly rather than clamped, so strays spread along the last stretch instead of stacking on it.
function fold(x, z, hold) {
  const m = Math.hypot(x, z);
  if (m <= hold) return [x, z];
  const t = (m - hold) / Math.max(1e-6, 1 - hold);
  const m2 = hold + (1 - hold) * Math.tanh(t);
  return [x * m2 / m, z * m2 / m];
}
export const RIM_HOLD = 0.45;        // where the falloff begins, as a fraction of the radius

const D = {
  cored:  (r) => 1 / (1 + Math.pow(r / 0.17, 1.7)),
  flat:   () => 1,
  expo:   (r) => Math.exp(-r / 0.30),
  sersic: (n) => { const b = 2 * n - 0.324; return (r) => Math.exp(-b * Math.pow(r / 0.42, 1 / n)); },
  broken: (rb, s1, s2) => (r) => r < rb ? Math.exp(-r / s1) : Math.exp(-rb / s1) * Math.exp(-(r - rb) / s2),
  plummer: (a) => (r) => Math.pow(1 + Math.pow(r / a, 2), -2.4),
};

// The shared log-spiral arm. A QUARTER of each arm's indices are held back for the inter-arm disc, because arms
// in a real galaxy are a density enhancement rather than the only place stars are — and a map whose stars exist
// only on arms has corridors of absolute emptiness between them, which is a navigation problem before it is a
// visual one.
function arm(i, arms, wind, spread, r, s) {
  if (i % 4 === 3) { const a = r() * TAU, d = s.rad(r()); return [Math.cos(a) * d, Math.sin(a) * d]; }
  const a0 = (i % arms) * TAU / arms, q = s.rad(r());
  const ang = s.tilt + a0 + wind * Math.log(1 + 6 * q) + gauss(r(), r()) * spread / (0.35 + q);
  return [Math.cos(ang) * q, Math.sin(ang) * q];
}

// `w` is how often a galaxy rolls this shape, per thousand. Hubble-sequence proportions for bright local
// galaxies, tempered in three places where the real frequency makes a worse map than an accurate catalogue:
// barred spirals lead because two thirds of real spirals carry a bar; dwarf spheroidals are by count the most
// numerous galaxies in the universe and are pulled well down because a dwarf is a cramped map; and the one
// non-physical form is kept at 0.3% rather than cut, because a shape nobody ever rolls does not exist.
// `sat`/`halo` are the expected companion counts — see companionsFor.
export const SHAPES = [
  { name: 'Cored Disc', w: 85, sat: 1.2, halo: 8, dens: () => D.cored,
    f: (i, N, r, s) => { const a = r() * TAU, d = s.rad(r()); return [Math.cos(a) * d, Math.sin(a) * d]; } },
  { name: 'Uniform Disc', w: 20, sat: 1.0, halo: 7, dens: () => D.flat,
    f: (i, N, r, s) => { const a = r() * TAU, d = s.rad(r()); return [Math.cos(a) * d, Math.sin(a) * d]; } },
  { name: 'Elliptical', w: 75, sat: 1.8, halo: 14, spins: false,   // dispersion-supported: a spinning oval claims rotational support it has not got dens: () => D.cored,
    f: (i, N, r, s) => { const a = r() * TAU, d = s.rad(r()), t = s.tilt;
      const x = Math.cos(a) * d, z = Math.sin(a) * d * s.q;
      return [x * Math.cos(t) - z * Math.sin(t), x * Math.sin(t) + z * Math.cos(t)]; } },
  { name: 'Exponential Disc', w: 65, sat: 1.2, halo: 8, dens: () => D.expo,
    f: (i, N, r, s) => { const a = r() * TAU, d = s.rad(r()); return [Math.cos(a) * d, Math.sin(a) * d]; } },
  { name: 'Sersic Disc', w: 38, sat: 1.4, halo: 10, dens: (s) => D.sersic(s.sersic),
    f: (i, N, r, s) => { const a = r() * TAU, d = s.rad(r()); return [Math.cos(a) * d, Math.sin(a) * d]; } },
  { name: 'Truncated Disc', w: 20, sat: 0.9, halo: 6, dens: (s) => D.broken(s.brk, 0.34, 0.34 / s.drop),
    f: (i, N, r, s) => { const a = r() * TAU, d = s.rad(r()); return [Math.cos(a) * d, Math.sin(a) * d]; } },
  { name: 'Boxy Elliptical', w: 15, sat: 1.8, halo: 14, spins: false,   // boxy isophotes ARE the signature of anisotropic dispersion, not of rotation dens: () => D.cored,
    f: (i, N, r, s) => { const a = r() * TAU, d = s.rad(r()), e = s.boxy, t = s.tilt;
      const ca = Math.cos(a), sa = Math.sin(a);
      const k = Math.pow(Math.pow(Math.abs(ca), e) + Math.pow(Math.abs(sa / s.q), e), -1 / e);
      const x = ca * d * k, z = sa * d * k;
      return [x * Math.cos(t) - z * Math.sin(t), x * Math.sin(t) + z * Math.cos(t)]; } },

  { name: 'Grand Design', w: 90, sat: 1.5, halo: 10, dens: () => D.cored,
    f: (i, N, r, s) => arm(i, 2, 2.6, 0.30, r, s) },
  { name: 'Multi-Arm', w: 105, sat: 1.4, halo: 9, dens: () => D.cored,
    f: (i, N, r, s) => arm(i, 4, 1.7, 0.34, r, s) },
  { name: 'Three-Arm', w: 42, sat: 1.3, halo: 9, dens: () => D.cored,
    f: (i, N, r, s) => arm(i, 3, 2.1, 0.31, r, s) },
  { name: 'Barred Spiral', w: 140, sat: 1.5, halo: 10, dens: () => D.cored,
    f: (i, N, r, s) => { const t = s.tilt, L = s.bar;
      if (i % 25 < 7) { const u = (r() * 2 - 1) * L, w = gauss(r(), r()) * 0.045;
        return [u * Math.cos(t) - w * Math.sin(t), u * Math.sin(t) + w * Math.cos(t)]; }
      if (i % 25 < 11) { const a = r() * TAU, d = s.rad(r()); return [Math.cos(a) * d, Math.sin(a) * d]; }
      const side = (i % 2) ? 1 : -1, q = s.rad(r()), d = L + q * (1 - L);
      const a = t + (side > 0 ? 0 : Math.PI) + 2.1 * Math.log(1 + 3.4 * q) + gauss(r(), r()) * 0.16;
      return [Math.cos(a) * d, Math.sin(a) * d]; } },
  { name: 'Lopsided', w: 28, sat: 1.0, halo: 7, dens: () => D.cored,
    f: (i, N, r, s) => { if (i % 5 >= 3) { const a = r() * TAU, d = s.rad(r());
        return [Math.cos(a) * d, Math.sin(a) * d]; }
      const q = s.rad(r()), a = s.tilt + 3.0 * Math.log(1 + 6 * q) + gauss(r(), r()) * 0.22;
      return [Math.cos(a) * q, Math.sin(a) * q]; } },
  { name: 'Whirlpool', w: 24, sat: 1.6, halo: 9, dens: () => D.cored,
    f: (i, N, r, s) => arm(i, 2, 5.4, 0.20, r, s) },
  { name: 'Pinwheel', w: 18, sat: 1.2, halo: 8, dens: () => D.cored,
    f: (i, N, r, s) => arm(i, 5, 0.85, 0.22, r, s) },
  { name: 'Spurred Spiral', w: 52, sat: 1.4, halo: 9, dens: () => D.cored,
    f: (i, N, r, s) => { if (i % 5 !== 4) return arm(i, 2, 2.6, 0.26, r, s);
      const sp = s.spur[i % 12], q = clampU(sp.q + gauss(r(), r()) * 0.05);
      const base = s.tilt + (sp.armE ? 0 : Math.PI) + 2.6 * Math.log(1 + 7 * Math.abs(q));
      const a = base + sp.dir * (0.12 + r() * 0.42);
      const d = Math.abs(q) * (0.9 + r() * 0.22);
      return fold(Math.cos(a) * d, Math.sin(a) * d, s.hold); } },

  { name: 'Clumpy Irregular', w: 38, sat: 0.3, halo: 4,
    f: (i, N, r, s) => { const c = s.clumps[i % 7];
      return fold(c.x + gauss(r(), r()) * c.s, c.z + gauss(r(), r()) * c.s, s.hold); } },
  { name: 'Magellanic', w: 32, sat: 0.2, halo: 3, dens: () => D.expo,
    f: (i, N, r, s) => { const t = s.tilt, ox = 0.16, oz = -0.09;
      if (i % 5 < 2) { const u = (r() * 2 - 1) * 0.40, w = gauss(r(), r()) * 0.05;
        return fold(ox + u * Math.cos(t) - w * Math.sin(t), oz + u * Math.sin(t) + w * Math.cos(t), s.hold); }
      const a = r() * TAU, d = s.rad(r()) * (0.55 + 0.45 * (0.5 + 0.5 * Math.cos(a - t)));
      return fold(ox + Math.cos(a) * d, oz + Math.sin(a) * d, s.hold); } },
  { name: 'Interacting Pair', w: 20, sat: 0.8, halo: 18,
    f: (i, N, r, s) => { const m = i % 20, sep = 0.46, t = s.tilt;
      const rot = (x, z) => fold(x * Math.cos(t) - z * Math.sin(t), x * Math.sin(t) + z * Math.cos(t), s.hold);
      if (m < 9)  return rot(-sep + gauss(r(), r()) * 0.20, gauss(r(), r()) * 0.20);
      if (m < 18) return rot( sep + gauss(r(), r()) * 0.17, gauss(r(), r()) * 0.17);
      const u = r() * 2 - 1;
      return rot(u * sep, (1 - u * u) * 0.15 + gauss(r(), r()) * 0.05); } },
  { name: 'Satellite Infall', w: 18, sat: 1.6, halo: 12, dens: () => D.cored,
    f: (i, N, r, s) => { const m = i % 8;
      if (m < 7) { const a = r() * TAU, d = s.rad(r()); return [Math.cos(a) * d, Math.sin(a) * d]; }
      const sa = s.tilt, sr = 0.62;
      if (i % 24 === 7) { const u = r(); const a = sa - u * 0.8, d = sr * (1 - u * 0.25);
        return fold(Math.cos(a) * d + gauss(r(), r()) * 0.03, Math.sin(a) * d + gauss(r(), r()) * 0.03, s.hold); }
      return fold(Math.cos(sa) * sr + gauss(r(), r()) * 0.075, Math.sin(sa) * sr + gauss(r(), r()) * 0.075, s.hold); } },
  { name: 'Tidal Stream', w: 9, sat: 0.7, halo: 16, dens: () => D.cored,
    f: (i, N, r, s) => { if (i % 5 !== 4) { const a = r() * TAU, d = s.rad(r());
        return [Math.cos(a) * d, Math.sin(a) * d]; }
      const u = r(), a = s.tilt + u * TAU * 1.6, d = 0.55 + u * 0.32;
      const w = gauss(r(), r()) * (0.020 + u * 0.035);
      return fold(Math.cos(a) * (d + w), Math.sin(a) * (d + w), s.hold); } },
  { name: 'Merger Remnant', w: 7, sat: 0.5, halo: 20, spins: false,   // the prototypical slow rotator, and its fans are debris rather than orbiting structure dens: () => D.sersic(2.6),
    f: (i, N, r, s) => { if (i % 5 < 3) { const a = r() * TAU, d = s.rad(r());
        return [Math.cos(a) * d, Math.sin(a) * d]; }
      const fn = s.fans[i % 3], u = r();
      const a = fn.a + (r() - 0.5) * fn.w * (0.35 + u), d = fn.r0 + u * fn.len;
      return fold(Math.cos(a) * d, Math.sin(a) * d, s.hold); } },

  { name: 'Dwarf Spheroidal', w: 42, sat: 0.05, halo: 1, spins: false,   // dispersion-supported — the same argument that keeps satellite interiors still dens: () => D.plummer(0.115),
    f: (i, N, r, s) => { const a = r() * TAU, d = s.rad(r()); return [Math.cos(a) * d, Math.sin(a) * d]; } },
  { name: 'Clustered Disc', w: 14, sat: 1.2, halo: 8, dens: () => D.cored,
    f: (i, N, r, s) => { if (i % 20 < 11) { const a = r() * TAU, d = s.rad(r());
        return [Math.cos(a) * d, Math.sin(a) * d]; }
      const c = s.assoc[i % s.assoc.length];
      return fold(c.x + gauss(r(), r()) * c.s, c.z + gauss(r(), r()) * c.s, s.hold); } },
  { name: 'Bubble Foam', w: 3, sat: 1.0, halo: 8, spins: false,   // no physical referent, and its analogue, the cosmic web, does not rotate coherently dens: () => D.cored,
    f: (i, N, r, s) => { const a0 = r() * TAU, d0 = s.rad(r());
      let x = Math.cos(a0) * d0, z = Math.sin(a0) * d0;
      for (let k = 0; k < 3; k++) {
        let b = null, c = null;
        for (const q of s.cells) { const dd = (q.x - x) * (q.x - x) + (q.z - z) * (q.z - z);
          if (!b || dd < b.d) { c = b; b = { p: q, d: dd }; } else if (!c || dd < c.d) c = { p: q, d: dd }; }
        const d1 = Math.sqrt(b.d), d2 = Math.sqrt(c.d), mid = (d1 + d2) / 2;
        x += (b.p.x - x) / (d1 || 1) * (d1 - mid) * 0.9;
        z += (b.p.z - z) / (d1 || 1) * (d1 - mid) * 0.9;
      }
      return fold(x + gauss(r(), r()) * 0.018, z + gauss(r(), r()) * 0.018, s.hold); } },
];

// How many arms a winding shape has — used by the suite to fold angles into one arm's period when it measures
// which way the thing is actually wound. A shape absent from this list has no handedness to get wrong.
// WHICH SHAPES TURN AT ALL. The split is rotation-supported against dispersion-supported: a disc is held up by
// ordered orbital motion, an elliptical or a dwarf spheroidal by the random velocities of its own stars. Giant
// ellipticals are the classic SLOW ROTATORS, with v/sigma well below one, and a dwarf spheroidal barely rotates —
// which is the very argument that keeps a satellite's interior still a few hundred lines up. Letting the same
// object type pinwheel when it happens to be the host rather than the companion was inconsistent.
//
// It is also a claim the picture cannot support. A featureless round shape rotating looks identical to one at
// rest, so spinning it asserts nothing; but a FLATTENED elliptical spun about the disc axis makes the whole oval
// wheel around, and that is visible, and it is wrong for a system whose flattening comes from anisotropic
// dispersion rather than from rotation.
//
// Not every early-type galaxy is a slow rotator — the ATLAS3D split puts most of them in the fast-rotator class —
// so this is a simplification chosen for what reads honestly at a glance, not a claim about all ellipticals.
export const STILL = SHAPES.filter((s) => s.spins === false).map((s) => s.name);
export function spinsFor(shape) { return shape.spins !== false; }
// The angle a galaxy's disc is turned by, or zero for a shape that should not turn. One call site, so app.js
// never has to remember the rule.
export function patternAngleFor(galaxy, shape, nowMs) {
  return spinsFor(shape) ? patternAngle(galaxy, nowMs) : 0;
}

export const ARMS = {
  'Grand Design': 2, 'Multi-Arm': 4, 'Three-Arm': 3, 'Barred Spiral': 2, 'Lopsided': 1,
  'Whirlpool': 2, 'Pinwheel': 5, 'Spurred Spiral': 2, 'Tidal Stream': 1,
};
export const WEIGHT_TOTAL = SHAPES.reduce((a, s) => a + s.w, 0);
export function shapeForGalaxy(galaxy) {
  let t = seeded(galaxy + '.shapepick')() * WEIGHT_TOTAL;
  for (const sh of SHAPES) { t -= sh.w; if (t <= 0) return sh; }
  return SHAPES[0];
}

// THE ONE ENTRY POINT for placing a star. The mirror is applied here rather than inside two dozen shape
// functions: negating z is an exact reflection of the whole galaxy, so the arms, the spurs, a stream's winding
// and the bar's lean all flip together and stay consistent with each other. Callers use this, never shape.f.
export function place(shape, i, N, rnd, s) {
  const p = shape.f(i, N, rnd, s);
  return s.chir < 0 ? [p[0], -p[1]] : p;
}

// Per-galaxy constants a shape may need, and the radial sampler built from its density.
export function shapeSeed(galaxy, shape, hold = RIM_HOLD) {
  const r = seeded(galaxy + '.shape.' + shape.name);
  const s = { q: 0.38 + r() * 0.54, tilt: r() * TAU, hold, chir: chiralityOf(galaxy),
    sersic: 0.7 + r() * 3.3, brk: 0.34 + r() * 0.22, drop: 3 + r() * 2, boxy: 1.3 + r() * 1.9,
    bar: 0.30 + r() * 0.22, clumps: [], spur: [], fans: [], assoc: [], cells: [] };
  for (let k = 0; k < 7; k++) s.clumps.push({ x: (r() * 2 - 1) * 0.60, z: (r() * 2 - 1) * 0.60, s: 0.09 + r() * 0.18 });
  for (let k = 0; k < 12; k++) s.spur.push({ q: 0.25 + r() * 0.65, dir: r() < 0.5 ? -1 : 1, armE: r() < 0.5 });
  for (let k = 0; k < 3; k++) s.fans.push({ a: r() * TAU, w: 0.5 + r() * 1.0, r0: 0.18 + r() * 0.22, len: 0.35 + r() * 0.4 });
  s.rad = radialSampler(shape.dens ? shape.dens(s) : D.cored, hold);
  for (let k = 0; k < 180; k++) { const a = r() * TAU, d = s.rad(r());
    s.assoc.push({ x: Math.cos(a) * d, z: Math.sin(a) * d, s: 0.012 + r() * 0.030 }); }
  for (let k = 0; k < 9; k++) s.cells.push({ x: (r() * 2 - 1) * 0.80, z: (r() * 2 - 1) * 0.80 });
  return s;
}

// ---------------------------------------------------------------------------------------------------- ROTATION
//
// Real stars orbit on a FLAT ROTATION CURVE: past the core the orbital speed barely changes with radius, so the
// angular rate w = v/r falls as 1/r and an inner star laps an outer one several times over. That is exactly the
// motion the star field must NOT have. Turn every star at its own rate and the arms wind into a coil, the bar
// shears into a disc, and — the part that actually matters here — stars move RELATIVE to one another, so two
// claimable systems can drift into each other and "4.2 pc away" changes while you read it.
//
// Real arms are not material anyway. They are density waves: the PATTERN turns rigidly at its own slow speed
// while stars pass in and out through it. So the disc turns as ONE RIGID BODY, which is both the defensible
// physics and a single group transform per frame rather than ten thousand position writes.
//
// The period is around a day and unique per galaxy, off the wall clock — the same unfrozen-time model the
// on-world sky uses, so every device agrees without anything being stored.
export const vCurve = (r) => Math.tanh(r / 0.18);
export const omega  = (r) => vCurve(r) / Math.max(r, 0.035);
export const OMEGA_REF = omega(0.6);
// WHICH WAY A GALAXY IS WOUND, and it is not a free choice alongside the spin.
//
// SPIRAL ARMS TRAIL. Essentially every spiral in the sky has arms sweeping backward from the direction of
// rotation; leading arms are a contested curiosity rather than a type. The arm formula winds one way
// (dtheta/dr > 0), and the pattern direction used to be rolled independently — so half of every winding shape
// came out with LEADING arms. Measured before the fix: 50.9% of galaxies roll a winding shape and 49.8% of those
// were leading, which is about a quarter of every galaxy in the game spinning wrong for its own geometry.
//
// So chirality is rolled per galaxy and the spin FOLLOWS it. Galaxies still differ in handedness — a real one
// looks S-wound or Z-wound depending which side you are on — they just always trail.
//
// The sign rule, worked out against Three's own rotation and pinned in the suite: rotation.y increasing DECREASES
// a star's placement angle, so omega = -dphi/dt. Trailing needs sign(dtheta/dr) opposite to sign(omega), which
// comes out as sign(dtheta/dr) == sign(dir). Mirroring the galaxy flips dtheta/dr, so dir must flip with it.
export function chiralityOf(galaxy) {
  return seeded(galaxy + '.chirality')() < 0.5 ? -1 : 1;
}
export function patternOf(galaxy) {
  const r = seeded(galaxy + '.pattern');
  return { hours: 18 + r() * 14, dir: chiralityOf(galaxy) };
}
export function patternAngle(galaxy, nowMs) {
  const p = patternOf(galaxy);
  return p.dir * (nowMs / 3600000) / p.hours * TAU;
}

// ---------------------------------------------------------------------------------- SATELLITES AND HALO SYSTEMS
//
// Both are REAL SYSTEMS from the same index pool, so a satellite is a contiguous address range you can travel to
// and the main disc simply has a percent or two fewer stars.
//
// HOW MANY depends on the SHAPE, because in reality it depends on the host's mass and history: a big spiral or an
// elliptical carries a retinue, a dwarf spheroidal is itself somebody's satellite, and a merger remnant has few
// surviving bound companions but a great deal of loose debris.
//
// ORIENTATION IS ISOTROPIC. Real satellites and halo stars are spread over the whole sphere, not strung round the
// equator — a flat disc inside a round halo is how a galaxy is built. cos(i) uniform is what makes directions
// uniform on the sphere rather than piling at the poles, and it gives retrograde orbits for free.
const isoInc = (u) => Math.acos(2 * u - 1);
// An orbital plane is FIXED in a spherically symmetric potential and the orbit precesses within it, so the plane
// is set once by (inclination, node) and only the in-plane angle advances.
function orient(u, v, inc, node, argp) {
  const ca = Math.cos(argp), sa = Math.sin(argp);
  const pu = u * ca - v * sa, pv = u * sa + v * ca;
  const y = pv * Math.sin(inc), w = pv * Math.cos(inc);
  const cn = Math.cos(node), sn = Math.sin(node);
  return [pu * cn - w * sn, y, pu * sn + w * cn];
}
export const DISC_CLEAR = 1.06;      // companions stay outside the faded rim
export const HALO_APO_MAX = 2.15;    // and inside what the camera can frame
export const MIN_SEP = 0.045;        // closest any two companions may ever come, in galaxy radii, in true 3D

// In a flat-curve potential an orbit is a precessing ROSETTE, not a closed ellipse: the radius oscillates at the
// epicyclic frequency k = sqrt(2)*w while the object drifts round at w.
export function haloPos(o, t) {
  const w = omega(o.a) / OMEGA_REF * o.dir, k = Math.SQRT2 * Math.abs(w);
  const th = o.th0 + w * t;
  const rr = o.a * (1 - o.e * Math.cos(k * t + o.ph0));
  return orient(Math.cos(th) * rr, Math.sin(th) * rr, o.inc, o.node, o.argp);
}
export function satPos(sc, t) {
  const th = sc.phase + omega(sc.a) / OMEGA_REF * t;
  return orient(Math.cos(th) * sc.a, Math.sin(th) * sc.a, sc.inc, sc.node, 0);
}
// The body frame a satellite's members live in: radial (toward the host), along-orbit, plane normal. A tidally
// locked satellite keeps its long axis on the host, so this frame turns rigidly once per orbit — distances inside
// are preserved exactly and the tidal elongation stays aimed. A dwarf spheroidal is dispersion-supported and
// barely rotates, so NO internal motion is the honest model; an ordered spin would be the invented one.
export function satFrame(sc, t) {
  const P = satPos(sc, t), Q = satPos(sc, t + 0.004);
  const rl = Math.hypot(P[0], P[1], P[2]) || 1;
  const rad = [P[0] / rl, P[1] / rl, P[2] / rl];
  let vel = [Q[0] - P[0], Q[1] - P[1], Q[2] - P[2]];
  const vl = Math.hypot(vel[0], vel[1], vel[2]) || 1;
  vel = [vel[0] / vl, vel[1] / vl, vel[2] / vl];
  const nrm = [rad[1] * vel[2] - rad[2] * vel[1], rad[2] * vel[0] - rad[0] * vel[2],
               rad[0] * vel[1] - rad[1] * vel[0]];
  return { P, rad, vel, nrm };
}

function poisson(mean, r) {
  if (mean <= 0) return 0;
  const L = Math.exp(-mean);
  let k = 0, prod = r();
  while (prod > L && k < 40) { k++; prod *= r(); }
  return k;
}
function rollHalo(r) {
  // BOTH ends are chosen and a, e derived. Bounding eccentricity alone let apocentres reach four times the
  // galaxy radius — realistic, since a real halo dwarfs its disc, and impossible to frame.
  const peri = DISC_CLEAR + 0.02 + r() * 0.26;
  const apo = 1.42 + r() * (HALO_APO_MAX - 1.42);
  const hi = Math.max(apo, peri + 0.05);
  return { a: (peri + hi) / 2, e: (hi - peri) / (hi + peri), inc: isoInc(r()), node: r() * TAU,
           argp: r() * TAU, th0: r() * TAU, ph0: r() * TAU, dir: r() < 0.5 ? -1 : 1 };
}
// THE NO-COLLISION GUARANTEE IS MEASURED, not proved. An earlier flat version had a proof — disjoint radial
// bands, valid for all time — but isotropic orbits cannot be shown disjoint by construction. So orbits are laid
// out, every pair's closest approach is measured over eight rotations, and any orbit that breaches MIN_SEP is
// re-rolled. Two orbits in 3D rarely come near each other, which is why this converges.
const SPAN = TAU * 8, STEPS = 420;
// THE SAME ARITHMETIC, INTO A BUFFER. check sampled every orbit at 420 steps and took a fresh array from
// orient for each one — 29 objects a step, 12,000 per call, and up to 61 calls for a galaxy whose companions
// need relaxing. A few million short-lived arrays per newly-seen galaxy is what made the neighbourhood build
// spike to three seconds at random: not the arithmetic but the garbage, collected in the middle of it, on top
// of the scene the crossing had just freed. Measured across six crossings before this: 1036, 3187, 215, 251,
// 2855, 492ms.
//
// These write into two module-level buffers instead. Identical expressions in identical order, because the
// result decides where real satellites sit: a Float64Array round-trips a double exactly, so the answers are
// bit-for-bit the ones the allocating version gave. Verified against a hash of 1,200 galaxies' layouts.
const _ckH = new Float64Array(32 * 3), _ckS = new Float64Array(4 * 3);
function orientInto(u, v, inc, node, argp, out, o) {
  const ca = Math.cos(argp), sa = Math.sin(argp);
  const pu = u * ca - v * sa, pv = u * sa + v * ca;
  const y = pv * Math.sin(inc), w = pv * Math.cos(inc);
  const cn = Math.cos(node), sn = Math.sin(node);
  out[o] = pu * cn - w * sn; out[o + 1] = y; out[o + 2] = pu * sn + w * cn;
}
function haloPosInto(o, t, out, at) {
  const w = omega(o.a) / OMEGA_REF * o.dir, k = Math.SQRT2 * Math.abs(w);
  const th = o.th0 + w * t;
  const rr = o.a * (1 - o.e * Math.cos(k * t + o.ph0));
  orientInto(Math.cos(th) * rr, Math.sin(th) * rr, o.inc, o.node, o.argp, out, at);
}
function satPosInto(sc, t, out, at) {
  const th = sc.phase + omega(sc.a) / OMEGA_REF * t;
  orientInto(Math.cos(th) * sc.a, Math.sin(th) * sc.a, sc.inc, sc.node, 0, out, at);
}
function check(sats, halo) {
  let d = 9, kind = null, j = -1;
  const nh = halo.length, ns = sats.length;
  for (let st = 0; st < STEPS; st++) {
    const t = st / STEPS * SPAN;
    for (let i = 0; i < nh; i++) haloPosInto(halo[i], t, _ckH, i * 3);
    for (let i = 0; i < ns; i++) satPosInto(sats[i], t, _ckS, i * 3);
    for (let i = 0; i < nh; i++) {
      const a = i * 3;
      for (let k = i + 1; k < nh; k++) {
        const b = k * 3;
        const ax = _ckH[a] - _ckH[b], ay = _ckH[a + 1] - _ckH[b + 1], az = _ckH[a + 2] - _ckH[b + 2];
        const v = Math.sqrt(ax * ax + ay * ay + az * az);
        if (v < d) { d = v; kind = 'hh'; j = k; }
      }
      for (let k = 0; k < ns; k++) {
        const b = k * 3;
        const bx = _ckH[a] - _ckS[b], by = _ckH[a + 1] - _ckS[b + 1], bz = _ckH[a + 2] - _ckS[b + 2];
        const v = Math.sqrt(bx * bx + by * by + bz * bz) - sats[k].half;
        if (v < d) { d = v; kind = 'hs'; j = i; }
      }
    }
    for (let i = 0; i < ns; i++) for (let k = i + 1; k < ns; k++) {
      const a = i * 3, b = k * 3;
      const cx = _ckS[a] - _ckS[b], cy = _ckS[a + 1] - _ckS[b + 1], cz = _ckS[a + 2] - _ckS[b + 2];
      const v = Math.sqrt(cx * cx + cy * cy + cz * cz) - sats[i].half - sats[k].half;
      if (v < d) { d = v; kind = 'ss'; j = k; }
    }
  }
  return { d: nh + ns < 2 ? 9 : d, kind, j };
}
// MEMOISED, BECAUSE A CROSSING ASKS FOR MOST OF THE SAME GALAXIES AGAIN. This is the most expensive function
// in the module — it relaxes up to 26 halo orbits and 3 satellites against each other over eight revolutions
// until nothing breaches MIN_SEP — and the neighbourhood field asks it for every galaxy in the three inner
// shells, 342 of them. Crossing ONE cell moves a 7x7x7 box of cells by one: 294 of the 343 overlap, so 86% of
// the answers are ones we already worked out. Measured, that is the difference between a one-second wait and a
// tenth of one on every galaxy you travel to.
//
// Safe to share the object: it is a pure function of (galaxy, shape) and every caller only reads it — the
// relaxation mutates `sats` before returning, never after. Capped with FIFO eviction so a long session of
// crossings cannot grow it without bound.
const _compMemo = new Map();
const COMP_MEMO_MAX = 1200;
export function companionsFor(galaxy, shape) {
  const key = galaxy + '|' + shape.name;
  const hit = _compMemo.get(key);
  if (hit) return hit;
  const out = buildCompanions(galaxy, shape);
  if (_compMemo.size >= COMP_MEMO_MAX) _compMemo.delete(_compMemo.keys().next().value);
  _compMemo.set(key, out);
  return out;
}
// THE SAME ANSWER, WORKED OUT ELSEWHERE (the owner's play review 2, Oct 24, s2): app.js's field worker (field-worker.mjs) runs
// companionsFor off the main thread for the neighbourhood's galaxies and hands back the two arrays; this puts them in the
// memo exactly as buildCompanions returns them (the clearance getter included), so the field's builder finds them there.
// A pure function of (galaxy, shape) in the same code on another thread: the same doubles, bit for bit.
export function companionsKnown(galaxy, shape) { return _compMemo.has(galaxy + '|' + shape.name); }
export function primeCompanions(galaxy, shape, sats, halo) {
  const key = galaxy + '|' + shape.name;
  if (_compMemo.has(key) || !Array.isArray(sats) || !Array.isArray(halo)) return;
  let _clear;
  if (_compMemo.size >= COMP_MEMO_MAX) _compMemo.delete(_compMemo.keys().next().value);
  _compMemo.set(key, { sats, halo, get clearance() { return _clear || (_clear = check(sats, halo)); } });
}
function buildCompanions(galaxy, shape) {
  const r = seeded(galaxy + '.companions.' + shape.name);
  const nSat = Math.min(3, poisson(shape.sat, r));
  const nHalo = Math.min(26, poisson(shape.halo, r));
  const sats = [];
  for (let k = 0; k < nSat; k++) {
    const size = 0.036 + r() * 0.030, half = size * 2.1;
    // The BODY must clear the disc, not the centre — adding the margin to the centre let a big satellite's
    // outskirts sit inside the rim in about one galaxy in forty.
    sats.push({ a: DISC_CLEAR + half + 0.04 + r() * 0.46, inc: isoInc(r()), node: r() * TAU, phase: r() * TAU,
                size, half, stretch: 1.5 + r() * 1.3, tail: 0.20 + r() * 0.22,
                share: 0.004 + r() * 0.008 });
  }
  const halo = [];
  for (let k = 0; k < nHalo; k++) halo.push(rollHalo(r));
  let worst = check(sats, halo);
  for (let pass = 0; pass < 60 && worst.d < MIN_SEP; pass++) {
    if (worst.kind === 'hh' || worst.kind === 'hs') halo[worst.j] = rollHalo(r);
    else if (worst.kind === 'ss') { sats[worst.j].a += 0.06; sats[worst.j].inc = isoInc(r()); }
    worst = check(sats, halo);
  }
  if (worst.d < MIN_SEP && worst.kind !== 'ss') halo.splice(worst.j, 1);
  // CLEARANCE ON DEMAND. This was a THIRD full check of every pair over eight orbits, run for every galaxy the
  // view builds, and nothing in the game ever reads it — only the suite that asserts no two companions breach
  // MIN_SEP. Computing it eagerly cost a whole extra sweep per galaxy, which at 1,716 galaxies in the
  // neighbourhood field is seconds of the wait before a galaxy appears. Memoised, so the suite still pays for
  // it once per galaxy and the game does not pay at all.
  let _clear;
  return { sats, halo, get clearance() { return _clear || (_clear = check(sats, halo)); } };
}

// ------------------------------------------------------------------------------------ WHERE A GALAXY ITSELF IS
//
// A galaxy id is a plain integer and has never meant anything spatially. Read the SAME integer as a rank in an
// origin-outward enumeration of a 3D lattice and it gains a position without changing its content: galaxy 1 sits
// at the origin, and rank grows through nested cube shells, so numerically close ids are spatially close and the
// space is unbounded because the integers are.
//
// Both directions are needed and both are cheap. id -> cell draws the map; cell -> id is what lets the map ask
// "which galaxy is in the cell next door" without searching.
export function galaxyCell(id) {
  const n = Math.max(0, Math.floor(id) - 1);            // rank 0 is the origin, so id 1 is the centre of space
  let k = 0;
  while ((2 * k + 1) ** 3 <= n) k++;                     // the shell this rank falls in
  if (k === 0) return [0, 0, 0];
  let o = n - (2 * k - 1) ** 3;                          // offset within the shell
  const side = 2 * k + 1, cap = side * side;
  if (o < cap) return [(o % side) - k, Math.floor(o / side) - k, -k];          // the z = -k face
  o -= cap;
  if (o < cap) return [(o % side) - k, Math.floor(o / side) - k, k];           // the z = +k face
  o -= cap;
  const ring = 8 * k, z = Math.floor(o / ring) - (k - 1);                      // then the rings between them
  let j = o % ring;
  if (j < side) return [-k, j - k, z];
  j -= side;
  if (j < side) return [k, j - k, z];
  j -= side;
  const inner = 2 * k - 1;
  if (j < inner) return [j - (k - 1), -k, z];
  j -= inner;
  return [j - (k - 1), k, z];
}
export function cellGalaxy(x, y, z) {
  x = Math.round(x); y = Math.round(y); z = Math.round(z);
  const k = Math.max(Math.abs(x), Math.abs(y), Math.abs(z));
  if (k === 0) return 1;
  const base = (2 * k - 1) ** 3, side = 2 * k + 1, cap = side * side;
  if (z === -k) return base + (x + k) + (y + k) * side + 1;
  if (z === k) return base + cap + (x + k) + (y + k) * side + 1;
  const ring = 8 * k, zo = (z + (k - 1)) * ring;
  let j;
  if (x === -k) j = (y + k);
  else if (x === k) j = side + (y + k);
  else if (y === -k) j = 2 * side + (x + (k - 1));
  else j = 2 * side + (2 * k - 1) + (x + (k - 1));
  return base + 2 * cap + zo + j + 1;
}

// ------------------------------------------------------------------------------- A GALAXY SEEN FROM OUTSIDE
//
// From inside, a galaxy is ten thousand clickable systems. From outside it is a smudge of light, and building it
// as ten thousand THREE.Groups to draw a smudge would cost more than the galaxy you are standing in. So a
// neighbour is a SILHOUETTE: a few hundred samples of the same shape, in one Points cloud, promoted to a real
// galaxy only when you travel to it.
//
// The samples come from the same shape function the real thing uses, so a neighbour looks like what it will
// become — a barred spiral reads as barred from outside, and diving in does not change your mind about it.
// MEMOISED, BECAUSE A CROSSING ASKS FOR THE SAME SILHOUETTES AGAIN. Pure in (galaxy, count), and the second
// largest cost in building the neighbourhood after companionsFor — measured around a second of a cold field,
// spread over the 342 inner-shell galaxies. Crossing ONE cell moves a 7x7x7 box by one, so 294 of the 343
// cells are unchanged and 86% of the answers are ones already worked out. Callers only ever READ the array.
//
// BOUNDED BY TOTAL FLOATS, not by entry count, because the entries differ by two orders of magnitude: a
// near-shell silhouette is 2,600 samples and an outer one is 4. 2.4M floats is about 9.6MB and holds roughly
// four neighbourhoods; evicting by count instead would either starve the near shells or blow the budget on
// them.
const _silMemo = new Map();
const SIL_MEMO_FLOATS = 2400000;
let _silMemoFloats = 0;
export function silhouette(galaxy, count = 420) {
  const key = galaxy + '|' + count;
  const hit = _silMemo.get(key);
  if (hit) return hit;
  return silMemoPut(key, buildSilhouette(galaxy, count));
}
// …and one worked out by the field worker (s2), into the same memo under the same budget.
export function silhouetteKnown(galaxy, count = 420) { return _silMemo.has(galaxy + '|' + count); }
export function primeSilhouette(galaxy, count, out) {
  const key = galaxy + '|' + count;
  if (_silMemo.has(key) || !(out instanceof Float32Array) || out.length !== count * 3) return;
  silMemoPut(key, out);
}
function silMemoPut(key, out) {
  _silMemo.set(key, out);
  _silMemoFloats += out.length;
  while (_silMemoFloats > SIL_MEMO_FLOATS && _silMemo.size > 1) {
    const k = _silMemo.keys().next().value;
    if (k === key) break;                       // never evict the answer we are about to hand back
    _silMemoFloats -= _silMemo.get(k).length;
    _silMemo.delete(k);
  }
  return out;
}
function buildSilhouette(galaxy, count) {
  const shape = shapeForGalaxy(galaxy);
  const s = shapeSeed(galaxy, shape);
  const out = new Float32Array(count * 3);
  // The real galaxy spreads its stars over N indices; a silhouette samples the same function on a stride through
  // that range, so the arms and clumps land where they will land rather than being re-rolled.
  const N = starsIn(galaxy);
  const stride = Math.max(1, Math.floor(N / count));
  for (let k = 0; k < count; k++) {
    const i = 1 + k * stride;
    const rnd = seeded(galaxy + '.' + i + '.place');
    const p = place(shape, i, N, rnd, s);
    out[k * 3] = p[0];
    out[k * 3 + 1] = (rnd() * 2 - 1) * 0.033;      // the same thin vertical spread the disc has
    out[k * 3 + 2] = p[1];
  }
  return out;
}
// The star count, duplicated from app.js's getStarCountForGalaxy so this module stays free of the shell. The
// suite pins the two together.
export function starsIn(galaxy) {
  return 1000 + Math.floor(seeded(galaxy + '.starcount')() * 9000);
}
// A galaxy's radius in the same units app.js uses, so a neighbour is drawn to the same scale as the one you are
// standing in rather than to an invented one.
export function radiusOf(galaxy) {
  return 3.0 * Math.sqrt(starsIn(galaxy) / 325);
}

// HOW A GALAXY IS ORIENTED IN SPACE. Discs are not coplanar — the Milky Way and M31 are tilted some 40 degrees
// apart — so a field of neighbours all face-on would read as a diagram rather than a sky. The orientation is
// seeded per galaxy, so a neighbour looks the same every time you see it.
//
// AND IT IS NOT ISOTROPIC ANY MORE: A DISC LIES ALONG ITS FILAMENT. Galaxies do not fall into the web from
// random directions, they fall in ALONG it, and their spin axes end up correlated with the local collapse —
// which is why a field placed on a web but oriented at random still reads as galaxies dropped onto a pattern
// rather than as galaxies the pattern made. So the spin axis is biased AWAY from the filament's direction,
// putting the filament in the plane of the disc: strung along it, not skewered by it.
//
// The bias is weighted by how much of a filament is actually there (filamentAxis's strength), so a galaxy in
// a void keeps the old uniform orientation instead of being given a direction its surroundings do not have.
// WEB_ALIGN is an exponent, not a cone: it leans the distribution without ever forbidding an angle, so no
// galaxy is impossible and the field never looks combed.
//
// `roll` is 0 now and that loses nothing. A disc's orientation is two numbers — where its pole points — and
// the third angle a general Euler triple carries is an in-plane phase, which discGroup already supplies from
// patternAngleFor. Keeping a random roll here only scrambled the pole the other two had just set.
const WEB_ALIGN = 2.2;
export function orientationOf(galaxy) {
  const r = seeded(galaxy + '.orient');
  const u = 2 * r() - 1, ph = r() * TAU;
  const c0 = galaxyCell(galaxy);
  const f = filamentAxis(c0[0], c0[1], c0[2]);
  // Cosine of the angle between the disc's pole and the filament. |u|^k with k > 1 crowds it toward zero,
  // which is the pole at a right angle to the filament — the disc lying along it.
  const cs = u * Math.abs(u) ** (WEB_ALIGN * f[3]);
  const sn = Math.sqrt(Math.max(0, 1 - cs * cs));
  // Any two unit vectors perpendicular to f. The seam-free choice of seed axis matters: crossing f with a
  // fixed axis collapses wherever f is parallel to it.
  const ax = Math.abs(f[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
  let e1 = [f[1] * ax[2] - f[2] * ax[1], f[2] * ax[0] - f[0] * ax[2], f[0] * ax[1] - f[1] * ax[0]];
  const l1 = Math.hypot(e1[0], e1[1], e1[2]) || 1; e1 = [e1[0] / l1, e1[1] / l1, e1[2] / l1];
  const e2 = [f[1] * e1[2] - f[2] * e1[1], f[2] * e1[0] - f[0] * e1[2], f[0] * e1[1] - f[1] * e1[0]];
  const cp = Math.cos(ph), sp = Math.sin(ph);
  const nx = cs * f[0] + sn * (cp * e1[0] + sp * e2[0]);
  const ny = cs * f[1] + sn * (cp * e1[1] + sp * e2[1]);
  const nz = cs * f[2] + sn * (cp * e1[2] + sp * e2[2]);
  // With roll 0 the YXZ triple puts the disc's pole at (sin inc sin node, cos inc, sin inc cos node), so these
  // two angles ARE the pole — inverted here rather than sampled, which is what makes the bias land exactly.
  return { inc: Math.acos(Math.max(-1, Math.min(1, ny))), node: Math.atan2(nx, nz), roll: 0 };
}

// WHERE A GALAXY SITS, in world units, relative to the galaxy you are standing in. Cell spacing is a multiple of
// the HOME galaxy's radius: the Local Group's separation-to-size ratio is about 25 for the Milky Way and M31, but
// at 25 a neighbour is a speck long before it is reachable, so this is pulled in to something a player can
// actually navigate. The number is one constant because it is a game-feel decision, not a measurement.
export const CELL_SPACING = 18.0;
// THE COSMIC WEB, which is the one thing a wide view of the sky is never without. Galaxies are not scattered
// evenly: they hang in sheets and filaments around voids, and the structure is obvious at a glance in any real
// deep survey. A cubic lattice has none of it, and — this took a measurement to learn — neither does a lattice
// with independent per-galaxy jitter: independent noise has no CORRELATION LENGTH, so every galaxy wanders its
// own way and the field stays uniform.
//
// Structure needs a FIELD. This is the Zel'dovich approximation, still the standard first-order answer for how
// structure actually forms: lay down a smooth random potential and let matter flow DOWN its gradient.
// Neighbouring galaxies sample almost the same gradient, so they drift together — which is exactly what opens
// voids and piles galaxies onto the surfaces where two streams meet.
//
// THE FIRST VERSION OF THIS HAD ALMOST NO STRUCTURE, and the reason is worth keeping. It capped the step's
// MAGNITUDE at 0.45 of a cell, so no galaxy could ever leave its own cell — and a void is galaxies LEAVING.
// Measured over a 25-cube: density contrast (sd/mean of the count within 2.5 cells) 0.069, counts 51-81 about
// a mean of 67, and not one point of space either empty or crowded. A crystal with rounded corners.
//
// What the cap was really for is SHELL CROSSING. The flow converges onto caustics — that is what a filament
// IS — and at a caustic the map stops being one-to-one, so galaxies land on top of each other: the raw field
// put pairs 0.02 of a cell apart, which renders as one galaxy that cannot be pointed at separately. The cap
// avoided that by refusing to move at all, which is why it had no structure.
//
// SO THE OVERLAP IS FIXED WHERE IT HAPPENS INSTEAD. The flow runs at full strength and a short relaxation
// afterwards pushes apart any pair closer than WEB_FLOOR. Measured at equal separation, that is worth six
// times the structure: contrast 0.449 against 0.069, counts 3-254, 6.7% of space void and 7.2% in dense
// nodes, and the neighbourhood of a typical galaxy measurably filamentary (the largest eigenvalue of its
// neighbours' direction tensor 0.495, where 0.333 is isotropic and 1.0 is a line).
//
// THREE OCTAVES, not one. A single correlation length gives one size of structure everywhere, which reads as
// texture rather than as a web. Real structure is a hierarchy — big voids bounded by sheets, sheets creased
// into filaments, filaments beaded with nodes — so the potential is summed over three node spacings an octave
// apart, the widest carrying the most amplitude because that is where the voids come from. Each spacing is a
// WHOLE number of cells: at a fractional one the galaxy lattice sometimes straddles the steepest part of the
// interpolant and gets driven together, which was measured at 0.05 of a cell before.
const WEB_OCTAVES = [[3, 1.00], [6, 1.90], [12, 3.00]];   // [cells per node, amplitude]
const WEB_PULL = 3.0;      // how hard the flow is run. Filament sharpness PEAKS here and falls off above 5:
                           // past the peak the flow scatters rather than organises. Measured, not chosen.
const WEB_STEP = 2.5;      // cells: the furthest the flow may carry any one galaxy. NOT a structure knob —
                           // it exists to bound WEB_PAD below, and it costs about 0.015 of contrast.
const WEB_FLOOR = 0.35;    // cells: how close two galaxies may sit once the relaxation has run. One cell is
                           // CELL_SPACING galaxy-radii, so this is about three radii — clearly two objects.
const WEB_PASSES = 3;      // relaxation passes. 2 leaves the closest pair at 0.18 of a cell, 3 at 0.24.
const WEB_JITTER = 0.40;   // independent noise on top of the flow, whose ONLY job is to null what is left of
                           // the lattice. Measured as the first Fourier mode at the cell frequency — 1.0 for a
                           // perfect lattice, 0.017 noise floor — the flow alone leaves 0.064, visible as a
                           // faint grid of evenly spaced dots through the voids; this takes it to 0.015, which
                           // is the floor. It is nearly free: contrast 0.541 -> 0.533, filaments 0.496 -> 0.486,
                           // and the closest pair actually IMPROVES (0.208 -> 0.265 of a cell), because scatter
                           // breaks up coincidences the relaxation would otherwise have to push apart. This
                           // number cannot be reasoned to — at 0.34 it was the ONLY thing separating a galaxy
                           // from its lattice point and it made no structure at all. It works here because the
                           // flow, not this, is what clusters the field.
// HOW FAR A BLOCK MUST REACH BEYOND WHAT IT ANSWERS FOR. The relaxation is not a local function: a galaxy's
// final place depends on its neighbours' places, which depend on theirs. A galaxy from cell j can only reach
// an interior cell if |j - c| < WEB_FLOOR + 2*WEB_STEP, and each pass widens that by twice the largest push.
// At 8 the answer is EXACT — the suite builds the same cells from three unrelated block centres and requires
// bit-identical results, because a universe that shifts depending on where you computed it from is not fixed.
// It is a WORST-CASE bound and it stays one. Sampled, a much shorter pad looks fine: contamination only
// travels along chains of galaxies actually in contact, and those are short, so eight cells at the block edge
// mostly agree with eight cells anywhere else. Mostly. Cutting it to 3 and measuring 343 cells found a
// disagreement of 1.3e-2 of a cell — sporadic, because it depends on whether a contact chain happens to
// straddle the boundary. A universe that shifts rarely and unreproducibly is worse than one that shifts
// always, so this is not tuned down on the strength of a sample that agreed.
const WEB_PAD = 8;
const WEB_SERVE = 18;      // cells a built block answers for. WEB_REACH of that is what one field needs, so the
                           // surplus is how far you may travel before another block is built — four crossings.

const _webPot = new Map();
// Keyed by a packed NUMBER, not a string. A block asks for this a million and a half times and hashing
// 'web.0:-3,7,-12' was more of the build than the arithmetic it fed.
const webPot = (o, x, y, z) => {
  const k = ((o * 2048 + x + 1024) * 2048 + y + 1024) * 2048 + z + 1024;
  let v = _webPot.get(k);
  if (v === undefined) { v = seeded('web.' + o + ':' + x + ',' + y + ',' + z)() * 2 - 1; _webPot.set(k, v); }
  return v;
};
// Smoothstep the interpolation weights, not linear: a linear blend leaves the gradient discontinuous at every
// node boundary, which draws straight creases across the field — a lattice again, by another route.
const webSmooth = (t) => t * t * (3 - 2 * t);
// The potential itself. Only the orientation code needs this; the flow takes the analytic gradient below.
export function webPhi(cx, cy, cz) {
  let v = 0;
  for (let o = 0; o < WEB_OCTAVES.length; o++) {
    const cell = WEB_OCTAVES[o][0], amp = WEB_OCTAVES[o][1];
    const fx = cx / cell, fy = cy / cell, fz = cz / cell;
    const x0 = Math.floor(fx), y0 = Math.floor(fy), z0 = Math.floor(fz);
    const tx = webSmooth(fx - x0), ty = webSmooth(fy - y0), tz = webSmooth(fz - z0);
    for (let i = 0; i <= 1; i++) for (let j = 0; j <= 1; j++) for (let k = 0; k <= 1; k++)
      v += amp * webPot(o, x0 + i, y0 + j, z0 + k) * (i ? tx : 1 - tx) * (j ? ty : 1 - ty) * (k ? tz : 1 - tz);
  }
  return v;
}
// WHERE THE FLOW WOULD CARRY THE GALAXY IN THIS CELL, before anything is done about overlaps. Analytic rather
// than finite-differenced: one pass over the eight corners yields all three partials, where differencing would
// need six more full evaluations of the same eight — and this runs tens of thousands of times per block.
// `src` is where the node values come from, and it exists ONLY so the block build can hand in a flat array
// instead of the Map. There is still one copy of the mathematics: a second transcription of a gradient is
// exactly the kind of thing that drifts silently, and the suite pins the two sources against each other.
export function webFlow(cx, cy, cz, out, src) {
  const pot = src || webPot;
  let gx = 0, gy = 0, gz = 0;
  for (let o = 0; o < WEB_OCTAVES.length; o++) {
    const cell = WEB_OCTAVES[o][0], amp = WEB_OCTAVES[o][1] / cell;   // /cell: d/d(cell) not d/d(node)
    const fx = cx / cell, fy = cy / cell, fz = cz / cell;
    const x0 = Math.floor(fx), y0 = Math.floor(fy), z0 = Math.floor(fz);
    const tx = fx - x0, ty = fy - y0, tz = fz - z0;
    const sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty), sz = tz * tz * (3 - 2 * tz);
    const dx = 6 * tx * (1 - tx), dy = 6 * ty * (1 - ty), dz = 6 * tz * (1 - tz);   // d(smoothstep)
    for (let i = 0; i <= 1; i++) { const wx = i ? sx : 1 - sx, ex = i ? dx : -dx;
      for (let j = 0; j <= 1; j++) { const wy = j ? sy : 1 - sy, ey = j ? dy : -dy;
        for (let k = 0; k <= 1; k++) { const wz = k ? sz : 1 - sz, ez = k ? dz : -dz;
          const pv = pot(o, x0 + i, y0 + j, z0 + k) * amp;
          gx += pv * ex * wy * wz; gy += pv * wx * ey * wz; gz += pv * wx * wy * ez;
        } } }
  }
  let ux = -gx * WEB_PULL, uy = -gy * WEB_PULL, uz = -gz * WEB_PULL;   // matter flows DOWN the potential
  const m = Math.hypot(ux, uy, uz);
  if (m > WEB_STEP) { const s = WEB_STEP / m; ux *= s; uy *= s; uz *= s; }
  out[0] = ux; out[1] = uy; out[2] = uz;
  return out;
}
// The dusting, keyed on the CELL rather than on the galaxy id: the block build asks for tens of thousands of
// these and an integer hash is four times cheaper than seeding a PRNG from a string for each one.
function webJit(x, y, z, s) {
  let h = (Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(z, 1442695041) ^ Math.imul(s, 2246822519)) | 0;
  h = Math.imul(h ^ (h >>> 15), 2246822519); h = Math.imul(h ^ (h >>> 13), 3266489917); h ^= h >>> 16;
  return ((h >>> 0) / 4294967296) * 2 - 1;
}
const _webPos = new Map();   // 'x,y,z' -> [dx,dy,dz], the FINAL displacement, relaxation included
// Build one block and keep the part of it that is exact. Everything in here is a pure function of the cell, so
// two blocks that overlap agree to the bit and it does not matter which one answered first.
function webBuild(ox, oy, oz) { const it = webBuildSteps(ox, oy, oz); while (!it.next().done); }
// THE SAME BUILD, A SLICE AT A TIME (the owner's play review 2, Oct 24, s2: "Split drawGalaxy and buildNeighbourhood into
// steps of at most 8 ms"): one block was 158 ms on the M1 and some 600 ms on a phone, all in the boot's one long frame. A
// generator over the very same loops, yielding every WEB_STEP_CELLS cells (and between the relaxation's passes), so the
// arithmetic, its order and its answers are webBuild's own; webBuild runs it to the end at once. Nothing it leaves half
// done is visible: _webPos is only written in its last loop, cell by cell as before.
const WEB_STEP_CELLS = 2048;
function* webBuildSteps(ox, oy, oz) {
  const HALF = WEB_SERVE + WEB_PAD, S = 2 * HALF + 1, n = S * S * S;
  const X = new Float64Array(n), Y = new Float64Array(n), Z = new Float64Array(n), g = [0, 0, 0];
  // THE NODES THIS BLOCK WILL TOUCH, pulled into flat arrays up front. The gradient reads eight of them per
  // octave per cell — a million and a half lookups for one block — and a Map.get was most of the build. There
  // are only a few thousand distinct nodes, so this is a rounding error of memory for a third of the time.
  const grids = [], lows = [], sides = [];
  for (let o = 0; o < WEB_OCTAVES.length; o++) {
    const cell = WEB_OCTAVES[o][0];
    const l = Math.floor((ox - HALF) / cell) - 1, hi = Math.floor((ox + HALF) / cell) + 2;
    const ly = Math.floor((oy - HALF) / cell) - 1, lz = Math.floor((oz - HALF) / cell) - 1;
    const side = Math.max(hi - l, Math.floor((oy + HALF) / cell) + 2 - ly, Math.floor((oz + HALF) / cell) + 2 - lz);
    const grid = new Float64Array(side * side * side);
    for (let a = 0; a < side; a++) {
      for (let b = 0; b < side; b++) for (let c = 0; c < side; c++) grid[(a * side + b) * side + c] = webPot(o, l + a, ly + b, lz + c);
      yield;
    }
    grids.push(grid); lows.push([l, ly, lz]); sides.push(side);
  }
  const fast = (o, x, y, z) => {
    const L = lows[o], side = sides[o];
    const a = x - L[0], b = y - L[1], c = z - L[2];
    // Outside the precomputed span can only happen if the span arithmetic above is wrong; fall back rather
    // than read a neighbouring node's value, which would be a silent, plausible, wrong answer.
    if (a < 0 || b < 0 || c < 0 || a >= side || b >= side || c >= side) return webPot(o, x, y, z);
    return grids[o][(a * side + b) * side + c];
  };
  for (let i = 0; i < S; i++) for (let j = 0; j < S; j++) for (let k = 0; k < S; k++) {
    const cx = ox - HALF + i, cy = oy - HALF + j, cz = oz - HALF + k, t = (i * S + j) * S + k;
    webFlow(cx, cy, cz, g, fast);
    X[t] = cx + g[0] + webJit(cx, cy, cz, 1) * WEB_JITTER;
    Y[t] = cy + g[1] + webJit(cx, cy, cz, 2) * WEB_JITTER;
    Z[t] = cz + g[2] + webJit(cx, cy, cz, 3) * WEB_JITTER;
    if ((t & (WEB_STEP_CELLS - 1)) === 0) yield;
  }
  // A FLAT bucket grid at the FLOOR's OWN scale, not the cell's, and a plain array rather than a Map. One
  // bucket per cell meant testing 27 galaxies to find the handful within a third of a cell; at this scale the
  // same 27 buckets hold about one galaxy between them, and the whole relaxation costs a fifth of what it did.
  const F = WEB_FLOOR, F2 = F * F, INV = 1 / F;
  const M = 3, G = Math.ceil(S * INV) + 2 * M, nb = G * G * G;
  const head = new Int32Array(nb), next = new Int32Array(n);
  const PX = new Float64Array(n), PY = new Float64Array(n), PZ = new Float64Array(n);
  const lo = -HALF - M * F;
  for (let pass = 0; pass < WEB_PASSES; pass++) {
    head.fill(-1);
    yield;
    for (let t = 0; t < n; t++) {
      const bx = ((X[t] - ox - lo) * INV) | 0, by = ((Y[t] - oy - lo) * INV) | 0, bz = ((Z[t] - oz - lo) * INV) | 0;
      if (bx < 0 || by < 0 || bz < 0 || bx >= G || by >= G || bz >= G) { next[t] = -1; continue; }
      const b = (bx * G + by) * G + bz; next[t] = head[b]; head[b] = t;
    }
    PX.fill(0); PY.fill(0); PZ.fill(0);
    yield;
    for (let t = 0; t < n; t++) {
      if ((t & (WEB_STEP_CELLS - 1)) === 0) yield;
      const qx = X[t], qy = Y[t], qz = Z[t];
      const bx = ((qx - ox - lo) * INV) | 0, by = ((qy - oy - lo) * INV) | 0, bz = ((qz - oz - lo) * INV) | 0;
      if (bx < 1 || by < 1 || bz < 1 || bx >= G - 1 || by >= G - 1 || bz >= G - 1) continue;
      for (let a = -1; a <= 1; a++) for (let b2 = -1; b2 <= 1; b2++) for (let c = -1; c <= 1; c++) {
        for (let u = head[((bx + a) * G + (by + b2)) * G + (bz + c)]; u !== -1; u = next[u]) {
          if (u === t) continue;
          const ddx = qx - X[u], ddy = qy - Y[u], ddz = qz - Z[u], d2 = ddx * ddx + ddy * ddy + ddz * ddz;
          if (d2 < F2 && d2 > 1e-18) { const d = Math.sqrt(d2), sc = (F - d) / d * 0.5;
            PX[t] += ddx * sc; PY[t] += ddy * sc; PZ[t] += ddz * sc; }
        }
      }
    }
    for (let t = 0; t < n; t++) { X[t] += PX[t]; Y[t] += PY[t]; Z[t] += PZ[t]; }
    yield;
  }
  for (let i = WEB_PAD; i < S - WEB_PAD; i++) {
    yield;
    for (let j = WEB_PAD; j < S - WEB_PAD; j++) for (let k = WEB_PAD; k < S - WEB_PAD; k++) {
      const cx = ox - HALF + i, cy = oy - HALF + j, cz = oz - HALF + k, t = (i * S + j) * S + k;
      const key = cx + ',' + cy + ',' + cz;
      if (!_webPos.has(key)) _webPos.set(key, [X[t] - cx, Y[t] - cy, Z[t] - cz]);
    }
  }
}
// Where the web has carried the galaxy in cell (cx,cy,cz), in cells. A pure function of the cell, so a galaxy
// is in the same place every time anyone looks — including from a neighbour's own view of the same patch.
export function webOffset(cx, cy, cz) {
  const key = cx + ',' + cy + ',' + cz;
  let v = _webPos.get(key);
  if (v === undefined) { webBuild(cx, cy, cz); v = _webPos.get(key); }
  return v;
}
// ASK FOR THE CENTRE FIRST. A block serves WEB_SERVE cells around wherever it was centred, so a field walked
// corner-first builds one block per corner it wanders out of — measured at four builds and two seconds for a
// single crossing.
//
// THE GUARD IS ON COVERAGE, NOT ON THE CENTRE CELL. Checking whether the centre was cached looked right and
// was useless: after a crossing the new home is one cell from the old one and therefore always cached, so the
// prime did nothing and the field walked out of the old block's reach anyway — the two-second crossing above
// is that bug, not the absence of a prime. What has to be true is that some built block reaches WEB_REACH
// cells around this one, which is the radius fieldOrder is about to ask for.
export const WEB_REACH = 14;   // must match app.js's FIELD_QUERY_CHEB; the suite pins the two together
const _webCentres = [];
export function webPrime(cx, cy, cz) {
  if (webPrimed(cx, cy, cz)) return;
  _webCentres.push([cx, cy, cz]);
  webBuild(cx, cy, cz);
}
export function webPrimed(cx, cy, cz) {
  for (const b of _webCentres)
    if (Math.max(Math.abs(cx - b[0]), Math.abs(cy - b[1]), Math.abs(cz - b[2])) <= WEB_SERVE - WEB_REACH) return true;
  return false;
}
// webPrime a slice at a time (s2): the boot steps it between frames before the galaxy view is made, so the field's walk
// (app.js fieldOrder) finds its block built. The centre is recorded only once the block is whole, so a build left part
// done promises nothing; one that a synchronous webPrime overtook simply finds every cell already written.
export function* webPrimeSteps(cx, cy, cz) {
  if (webPrimed(cx, cy, cz)) return;
  yield* webBuildSteps(cx, cy, cz);
  if (!webPrimed(cx, cy, cz)) _webCentres.push([cx, cy, cz]);
}
// WHICH WAY THE FILAMENT RUNS HERE, and how much of one there is. The deformation tensor is the negative
// Hessian of the potential — the thing whose eigenvalues decide, in Zel'dovich, whether a patch collapses to a
// sheet (one axis), a filament (two) or a node (three). The axis matter does NOT collapse along is the
// filament's own direction, so that is the eigenvector of the LARGEST eigenvalue.
//
// The strength is how cleanly that axis is singled out: 1 where the other two eigenvalues are equal, which is
// a true filament, and 0 where the top two are equal and there is no line to speak of. It exists so the
// alignment below can be honest — a galaxy sitting in a void has no filament to lie along, and pretending it
// does would impose a direction on the one place with none.
const _webAxis = new Map();
export function filamentAxis(cx, cy, cz) {
  const key = cx + ',' + cy + ',' + cz;
  let v = _webAxis.get(key);
  if (v !== undefined) return v;
  const h = 0.25, P = (a, b, c) => webPhi(cx + a, cy + b, cz + c), c0 = P(0, 0, 0);
  const px = P(h, 0, 0), mx = P(-h, 0, 0), py = P(0, h, 0), my = P(0, -h, 0), pz = P(0, 0, h), mz = P(0, 0, -h);
  const xx = -(px - 2 * c0 + mx) / (h * h), yy = -(py - 2 * c0 + my) / (h * h), zz = -(pz - 2 * c0 + mz) / (h * h);
  const xy = -(P(h, h, 0) - P(h, -h, 0) - P(-h, h, 0) + P(-h, -h, 0)) / (4 * h * h);
  const xz = -(P(h, 0, h) - P(h, 0, -h) - P(-h, 0, h) + P(-h, 0, -h)) / (4 * h * h);
  const yz = -(P(0, h, h) - P(0, h, -h) - P(0, -h, h) + P(0, -h, -h)) / (4 * h * h);
  const M = [[xx, xy, xz], [xy, yy, yz], [xz, yz, zz]];
  const q = (xx + yy + zz) / 3, p1 = xy * xy + xz * xz + yz * yz;
  let e0, e1, e2;
  if (p1 < 1e-18) { const d = [xx, yy, zz].slice().sort((a, b) => b - a); e0 = d[0]; e1 = d[1]; e2 = d[2]; }
  else {
    const p2 = (xx - q) ** 2 + (yy - q) ** 2 + (zz - q) ** 2 + 2 * p1, pp = Math.sqrt(p2 / 6);
    const B = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
    for (let a = 0; a < 3; a++) for (let b = 0; b < 3; b++) B[a][b] = (M[a][b] - (a === b ? q : 0)) / pp;
    const det = B[0][0] * (B[1][1] * B[2][2] - B[1][2] * B[2][1]) - B[0][1] * (B[1][0] * B[2][2] - B[1][2] * B[2][0])
              + B[0][2] * (B[1][0] * B[2][1] - B[1][1] * B[2][0]);
    const ph = Math.acos(Math.max(-1, Math.min(1, det / 2))) / 3;
    e0 = q + 2 * pp * Math.cos(ph); e2 = q + 2 * pp * Math.cos(ph + 2 * Math.PI / 3); e1 = 3 * q - e0 - e2;
  }
  // The eigenvector of e0, as the cross product of two rows of (M - e0 I) — the pair furthest from parallel.
  const A = [[xx - e0, xy, xz], [xy, yy - e0, yz], [xz, yz, zz - e0]];
  const cr = (u, w) => [u[1] * w[2] - u[2] * w[1], u[2] * w[0] - u[0] * w[2], u[0] * w[1] - u[1] * w[0]];
  let best = null, bl = 0;
  for (const [i, j] of [[0, 1], [0, 2], [1, 2]]) { const c = cr(A[i], A[j]), nn = Math.hypot(c[0], c[1], c[2]);
    if (nn > bl) { bl = nn; best = c; } }
  const span = e0 - e2;
  const strength = span > 1e-12 ? Math.max(0, Math.min(1, (e0 - e1) / span)) : 0;
  v = (best && bl > 1e-12) ? [best[0] / bl, best[1] / bl, best[2] / bl, strength] : [0, 1, 0, 0];
  _webAxis.set(key, v);
  return v;
}
export function neighbourOffset(homeId, otherId, homeRadius) {
  const a = galaxyCell(homeId), b = galaxyCell(otherId);
  const wa = webOffset(a[0], a[1], a[2]), wb = webOffset(b[0], b[1], b[2]);
  const s = homeRadius * CELL_SPACING;
  // The HOME galaxy's own displacement is subtracted, so it stays at the origin of its own view and everything
  // else is placed relative to it. Otherwise the galaxy you are standing in would sit off-centre in its own
  // map, and the same patch of sky would look different depending on which galaxy you were looking from —
  // which is the one thing a fixed universe must never do.
  return [(b[0] - a[0] + wb[0] - wa[0]) * s,
          (b[1] - a[1] + wb[1] - wa[1]) * s,
          (b[2] - a[2] + wb[2] - wa[2]) * s];
}
// The ids around a home galaxy, nearest cell first, out to a Chebyshev radius. Every cell holds a galaxy —
// cellGalaxy is total — so a neighbourhood is never sparse and never needs a search.
export function neighboursOf(homeId, chebyshev = 2) {
  const [hx, hy, hz] = galaxyCell(homeId), out = [];
  for (let dx = -chebyshev; dx <= chebyshev; dx++)
    for (let dy = -chebyshev; dy <= chebyshev; dy++)
      for (let dz = -chebyshev; dz <= chebyshev; dz++) {
        if (!dx && !dy && !dz) continue;
        const id = cellGalaxy(hx + dx, hy + dy, hz + dz);
        if (id >= 1) out.push({ id, d: Math.hypot(dx, dy, dz), cell: [hx + dx, hy + dy, hz + dz] });
      }
  out.sort((p, q) => p.d - q.d);
  return out;
}

// HOW A GALAXY IS COLOURED. The colour-morphology relation is a real one and it is the reason a field of
// galaxies is not monochrome: a dispersion-supported galaxy — an elliptical, a dwarf spheroidal — has no gas
// left and therefore no young blue stars, so only the old red population remains. A disc is still forming
// stars and reads bluer, bluest along the arms where the young ones are. That means the field teaches itself:
// the warm compact smudges are the ellipticals, and you can tell what a galaxy is before you read its label.
//
// TWO FIXED HUES WOULD NOT DO IT. A hundred galaxies in two colours reads as two decals repeated, which is
// exactly how the first version looked. So each galaxy jitters around its own anchor, and a minority are
// genuinely distinctive: about one disc in eleven is a STARBURST — bluer and appreciably brighter, because it
// is converting gas into hot young stars far faster than a quiet disc — and about one in nine is DUSTY, where
// reddening by its own dust lane warms it toward the ellipticals without making it one.
//
// The ellipticals' spread is deliberately the narrower of the two. Real early-type galaxies sit on a tight
// red sequence; the discs are the scattered ones, because how blue a disc looks depends on how much star
// formation it happens to be doing.
// TWO ANCHORS, and everything stellar in this file is a spread around one of them. A population either still
// has gas and is making hot young stars, or it does not and only the old red ones are left; there is no third
// place for a galaxy's colour to be. Naming them is not tidiness — it is what stops the next thing that needs
// "the red one" from reinventing it. Interpolating BETWEEN them is the specific trap: hue is a wheel, and a
// straight blend from blue to red runs through GREEN the long way round, which is how a satellite of a dusty
// spiral came out grass-coloured. Pick an anchor and spread around it; never average two.
export const POP_BLUE = 0.575;   // gas-rich, forming stars
export const POP_RED  = 0.065;   // old, stripped, done
export function paletteOf(galaxy) {
  const r = seeded(galaxy + '.palette');
  const roll = r();
  if (!spinsFor(shapeForGalaxy(galaxy)))
    return { hue: POP_RED - 0.010 + r() * 0.050, sat: 0.72 + r() * 0.16, gain: 0.92 + r() * 0.16, kind: 'old' };
  if (roll < 0.09)  return { hue: POP_BLUE - 0.010 + r() * 0.035, sat: 0.62 + r() * 0.14, gain: 1.34 + r() * 0.20, kind: 'starburst' };
  if (roll < 0.20)  return { hue: POP_RED + 0.020 + r() * 0.045, sat: 0.50 + r() * 0.16, gain: 0.88 + r() * 0.12, kind: 'dusty' };
  return { hue: POP_BLUE - 0.040 + r() * 0.075, sat: 0.46 + r() * 0.20, gain: 1.00 + r() * 0.22, kind: 'disc' };
}
// A COMPANION'S OWN COLOUR, which is a property of the companion and not of what it orbits. A gas-rich
// irregular is blue wherever it is — the Magellanic Clouds are — and a dwarf spheroidal that has been stripped
// is red. Two thirds irregular, roughly the local mix. Drawn off the stream passed in, so the field and the
// card consume it in the same order and place the same satellites.
// A SATELLITE'S MEMBERS, and the reason the draw is BOUNDED. A Gaussian has no largest value: at two thousand
// draws one will land four sigma out, and a member four sigma out is both a star further from its dwarf than
// the dwarf is wide and — for anything that has to FRAME a galaxy — an unpredictable overhang. The card fits
// its frame to the drawn extent, and an unbounded tail meant one star's sprite hanging over the edge, which is
// the single thing that framing exists to prevent. Clamped, the extent is exactly computable: satSpread says
// how far a satellite can reach, and the two agree because they are the same constant.
export const SAT_SIGMA = 2.6;
export function satGauss(r) {
  const g = Math.sqrt(-2 * Math.log(1e-9 + r())) * Math.cos(TAU * r());
  return Math.max(-SAT_SIGMA, Math.min(SAT_SIGMA, g));
}
// The furthest a satellite's light can reach from its centre, in host radii: the three clamped axes in
// quadrature, plus the sprite radius of the point itself.
export function satSpread(sc) {
  return SAT_SIGMA * sc.size * Math.hypot(sc.stretch * 0.5, 0.42, 0.42) + sc.size * 0.15;
}
export function satPalette(r) {
  const irr = r() < 0.66;
  return irr ? { hue: POP_BLUE - 0.030 + r() * 0.055, lit: 0.085, kind: 'irregular' }
             : { hue: POP_RED - 0.010 + r() * 0.045, lit: 0.055, kind: 'spheroidal' };
}
